import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table"
import PropTypes from "prop-types"
import { useState, useEffect, useRef, useMemo } from "react"
import { Loader2 } from "lucide-react"
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
} from "../Icon"
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import "./AppTable.scss"

// Row click navigates to primary detail page.
// Interactive elements inside cells (buttons, links, toggles) must call
// e.stopPropagation() to prevent row navigation when clicked.
// This is the gold standard for all tables with primary navigation.
//
// Also ignore clicks that originate inside known cell controls — some browsers /
// touch paths do not reliably bubble stopPropagation from nested interactive UI.
function isAppTableRowClickSuppressed(target) {
  if (!target?.closest) return false
  return Boolean(
    target.closest(
      "[data-app-table-row-click-ignore], button.account-name-link, a[href]",
    ),
  )
}

// ── Feature 4c: Horizontal scroll drag (grab-to-scroll) ──────────────────────
// Pure mouse event listeners — no library dependency.
function useScrollDrag(ref) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let isDown = false
    let startX = 0
    let scrollLeft = 0

    const onMouseDown = (e) => {
      if (el.scrollWidth <= el.clientWidth) return
      if (
        e.target.closest(
          "a, button, input, select, textarea, [role='button'], [data-radix-collection-item]",
        )
      )
        return
      isDown = true
      startX = e.pageX - el.offsetLeft
      scrollLeft = el.scrollLeft
      el.style.cursor = "grabbing"
      el.style.userSelect = "none"
    }
    const onMouseUp = () => {
      if (!isDown) return
      isDown = false
      el.style.cursor = ""
      el.style.userSelect = ""
    }
    const onMouseMove = (e) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - el.offsetLeft
      el.scrollLeft = scrollLeft - (x - startX)
    }

    el.addEventListener("mousedown", onMouseDown)
    el.addEventListener("mouseup", onMouseUp)
    el.addEventListener("mousemove", onMouseMove)
    el.addEventListener("mouseleave", onMouseUp)

    return () => {
      el.removeEventListener("mousedown", onMouseDown)
      el.removeEventListener("mouseup", onMouseUp)
      el.removeEventListener("mousemove", onMouseMove)
      el.removeEventListener("mouseleave", onMouseUp)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- ref is stable
}

// ── Feature 4b: Sortable header cell for column reorder ──────────────────────
function SortableHeaderCell({ id, children, style: extraStyle, ...rest }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  const style = {
    ...extraStyle,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 10 : "auto",
  }
  return (
    <th
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="columnheader"
      {...rest}
    >
      {children}
    </th>
  )
}

export default function AppTable({
  columns,
  data = [],
  loading = false,
  onRowClick,
  onRowMouseEnter,
  emptyMessage = "Nothing here",
  className = "",
  initialSorting = [],
  mobileCards = false,
  pageSize = 30,
  serverPagination,
  enableColumnReorder = false,
  tableId,
  "data-testid": dataTestId,
}) {
  const [sorting, setSorting] = useState(initialSorting)
  const [autofitCols, setAutofitCols] = useState({})

  // Feature 4c: scroll drag ref
  const scrollRef = useRef(null)
  useScrollDrag(scrollRef)

  // Feature 4b: column order state with localStorage persistence
  const [columnOrder, setColumnOrder] = useState(() => {
    if (!enableColumnReorder || !tableId) return null
    try {
      const saved = localStorage.getItem(`appTable_${tableId}_columnOrder`)
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  // Feature 4b: dnd-kit sensors with activation delay to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  )

  // Only activate card JSX at mobile breakpoints — CSS and JSX must agree on the cutoff
  const [isMobileView, setIsMobileView] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return false
    return window.matchMedia("(max-width: 768px)").matches
  })
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return
    const mq = window.matchMedia("(max-width: 768px)")
    const handler = (e) => setIsMobileView(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  // Pagination: pageSize > 0 enables client-side pagination; 0 = show all rows
  const enablePagination = pageSize > 0 && !serverPagination

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(serverPagination
        ? {
            pagination: {
              pageIndex: serverPagination.page,
              pageSize: pageSize || data.length,
            },
          }
        : {}),
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enablePagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(enablePagination
      ? {
          initialState: { pagination: { pageSize } },
        }
      : {}),
    ...(serverPagination
      ? {
          manualPagination: true,
          pageCount: Math.ceil(serverPagination.totalCount / (pageSize || 30)),
        }
      : {}),
  })

  // Toggle autofit for a column (desktop only)
  const handleHeaderDoubleClick = (colId) => {
    setAutofitCols((prev) => {
      const next = { ...prev }
      if (next[colId]) {
        delete next[colId]
      } else {
        next[colId] = true
      }
      return next
    })
  }

  // Build a lookup: columnId → header string (for data-label on mobile cards)
  const headerLabels = {}
  table.getAllColumns().forEach((col) => {
    const h = col.columnDef.header
    headerLabels[col.id] = typeof h === "string" ? h : col.id
  })

  // ── CARD VIEW CONFIG (mobileCards prop) ──────────────────────────
  // Column meta options:
  //   meta: { isStatusCol: true }     → badge shown right side of title row
  //   meta: { truncateInCard: true }  → value truncates with ellipsis in card mode
  //   meta: { hideInCard: true }      → column excluded from card mode entirely
  // ──────────────────────────────────────────────────────────────────

  // ── Feature 4b: Compute ordered headers respecting saved column order ──────
  const headerGroup = table.getHeaderGroups()[0]
  const orderedHeaders = useMemo(() => {
    if (!columnOrder || !enableColumnReorder) return headerGroup.headers
    const headerMap = new Map(headerGroup.headers.map((h) => [h.id, h]))
    const ordered = columnOrder
      .filter((id) => headerMap.has(id))
      .map((id) => headerMap.get(id))
    // Append any new columns not yet in saved order
    headerGroup.headers.forEach((h) => {
      if (!columnOrder.includes(h.id)) ordered.push(h)
    })
    return ordered
  }, [columnOrder, headerGroup.headers, enableColumnReorder])

  // ── Feature 4b: Build a stable column-id → position map for body row reorder
  const orderedColumnIds = useMemo(
    () => orderedHeaders.map((h) => h.column.id),
    [orderedHeaders],
  )

  // ── Feature 4b: Handle drag end — update order state + localStorage ────────
  function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = orderedHeaders.map((h) => h.id)
    const oldIndex = ids.indexOf(active.id)
    const newIndex = ids.indexOf(over.id)
    const newOrder = arrayMove(ids, oldIndex, newIndex)
    setColumnOrder(newOrder)
    if (tableId) {
      localStorage.setItem(
        `appTable_${tableId}_columnOrder`,
        JSON.stringify(newOrder),
      )
    }
  }

  // ── Shared header cell renderer ───────────────────────────────────────────
  function renderHeaderCell(header) {
    const sorted = header.column.getIsSorted()
    const canSort = header.column.getCanSort()
    const thProps = {
      onClick: header.column.getToggleSortingHandler(),
      onDoubleClick: () => handleHeaderDoubleClick(header.column.id),
      className: [
        `col-${header.column.id}`,
        canSort ? "app-table__th--sortable" : "",
        sorted ? "app-table__th--sorted" : "",
      ]
        .filter(Boolean)
        .join(" "),
      style: {
        ...(header.column.columnDef.meta?.align
          ? { textAlign: header.column.columnDef.meta.align }
          : {}),
      },
    }

    const getSortIcon = () => {
      if (sorted === "asc") return <ChevronUp size={12} />
      if (sorted === "desc") return <ChevronDown size={12} />
      return <ArrowUpDown size={11} />
    }

    const inner = (
      <span className="app-table__th-inner">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {canSort && (
          <span className={`app-table__sort-icon${sorted ? " is-active" : ""}`}>
            {getSortIcon()}
          </span>
        )}
      </span>
    )

    if (enableColumnReorder) {
      return (
        <SortableHeaderCell key={header.id} id={header.id} {...thProps}>
          {inner}
        </SortableHeaderCell>
      )
    }

    return (
      <th key={header.id} {...thProps}>
        {inner}
      </th>
    )
  }

  // ── Shared header row content ─────────────────────────────────────────────
  function renderHeaderRow() {
    return orderedHeaders.map((header) => renderHeaderCell(header))
  }

  // ── Wrap content in DndContext when reorder is enabled.
  // DndContext renders only React context — no extra DOM nodes injected.
  // Placed outside <table> to stay valid with HTML table structure.
  const wrapWithDnd = (content) => {
    if (!enableColumnReorder) return content
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {content}
      </DndContext>
    )
  }

  if (loading) {
    return (
      <div className="app-table-loading">
        <Loader2
          className="animate-spin text-[var(--text-secondary)]"
          size={24}
        />
      </div>
    )
  }

  return wrapWithDnd(
    <div
      ref={scrollRef}
      className={`app-table-wrap${mobileCards ? " app-table-wrap--mobile-cards" : ""} ${className}`}
      data-testid={dataTestId}
    >
      <table className="app-table">
        <colgroup>
          {table.getAllColumns().map((col) => (
            <col
              key={col.id}
              style={
                col.columnDef.size && !autofitCols[col.id]
                  ? { width: col.columnDef.size }
                  : undefined
              }
              {...(autofitCols[col.id] ? { "data-autofit": "" } : {})}
            />
          ))}
        </colgroup>
        <thead>
          {enableColumnReorder ? (
            <SortableContext
              items={orderedHeaders.map((h) => h.id)}
              strategy={horizontalListSortingStrategy}
            >
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>{renderHeaderRow()}</tr>
              ))}
            </SortableContext>
          ) : (
            table
              .getHeaderGroups()
              .map((hg) => <tr key={hg.id}>{renderHeaderRow()}</tr>)
          )}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr className="app-table__empty-row">
              <td colSpan={columns.length}>{emptyMessage}</td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              if (mobileCards && isMobileView) {
                const visibleCells = row.getVisibleCells()
                const titleCell = visibleCells.find(
                  (cell) => !cell.column.columnDef.meta?.isStatusCol,
                )
                const statusCell = visibleCells.find(
                  (cell) => cell.column.columnDef.meta?.isStatusCol === true,
                )
                const bodyCells = visibleCells.filter(
                  (cell) =>
                    cell !== titleCell &&
                    cell !== statusCell &&
                    !cell.column.columnDef.meta?.hideInCard,
                )

                return (
                  <tr
                    key={row.id}
                    onClick={(e) => {
                      if (isAppTableRowClickSuppressed(e.target)) return
                      onRowClick?.(row.original)
                    }}
                    onMouseEnter={() => onRowMouseEnter?.(row.original)}
                    className={onRowClick ? "app-table__row--clickable" : ""}
                  >
                    <td
                      className="app-table__card-header"
                      data-label={
                        titleCell ? headerLabels[titleCell.column.id] : ""
                      }
                    >
                      <span className="app-table__card-title">
                        {titleCell &&
                          flexRender(
                            titleCell.column.columnDef.cell,
                            titleCell.getContext(),
                          )}
                      </span>
                      {statusCell && (
                        <span className="app-table__card-status">
                          {flexRender(
                            statusCell.column.columnDef.cell,
                            statusCell.getContext(),
                          )}
                        </span>
                      )}
                    </td>
                    {bodyCells.map((cell) => (
                      <td
                        key={cell.id}
                        className={[
                          `col-${cell.column.id}`,
                          cell.column.columnDef.meta?.truncateInCard
                            ? "app-table__card-cell--truncate"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        data-label={headerLabels[cell.column.id]}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                )
              }

              // Desktop: reorder td cells to match header order when enableColumnReorder is active
              const visibleCells = row.getVisibleCells()
              const cellsToRender = enableColumnReorder
                ? orderedColumnIds
                    .map((colId) =>
                      visibleCells.find((c) => c.column.id === colId),
                    )
                    .filter(Boolean)
                : visibleCells

              return (
                <tr
                  key={row.id}
                  onClick={(e) => {
                    if (isAppTableRowClickSuppressed(e.target)) return
                    onRowClick?.(row.original)
                  }}
                  onMouseEnter={() => onRowMouseEnter?.(row.original)}
                  className={onRowClick ? "app-table__row--clickable" : ""}
                >
                  {cellsToRender.map((cell) => (
                    <td
                      key={cell.id}
                      className={`col-${cell.column.id}`}
                      data-label={headerLabels[cell.column.id]}
                      style={{
                        width: cell.column.getSize(),
                        ...(cell.column.columnDef.meta?.align
                          ? { textAlign: cell.column.columnDef.meta.align }
                          : {}),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>

      {/* Client-side pagination controls */}
      {enablePagination && table.getPageCount() > 1 && (
        <div
          className="app-table-pagination"
          data-testid="app-table-pagination"
        >
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="app-table-pagination__info">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Server-side pagination controls */}
      {serverPagination &&
        Math.ceil(serverPagination.totalCount / (pageSize || 30)) > 1 && (
          <div
            className="app-table-pagination"
            data-testid="app-table-pagination"
          >
            <button
              onClick={() =>
                serverPagination.onPageChange(serverPagination.page - 1)
              }
              disabled={serverPagination.page <= 0}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="app-table-pagination__info">
              Page {serverPagination.page + 1} of{" "}
              {Math.ceil(serverPagination.totalCount / (pageSize || 30))}
            </span>
            <button
              onClick={() =>
                serverPagination.onPageChange(serverPagination.page + 1)
              }
              disabled={
                serverPagination.page >=
                Math.ceil(serverPagination.totalCount / (pageSize || 30)) - 1
              }
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
    </div>,
  )
}

AppTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  onRowMouseEnter: PropTypes.func,
  emptyMessage: PropTypes.node,
  className: PropTypes.string,
  initialSorting: PropTypes.array,
  mobileCards: PropTypes.bool,
  pageSize: PropTypes.number,
  serverPagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
  }),
  enableColumnReorder: PropTypes.bool,
  tableId: PropTypes.string,
  "data-testid": PropTypes.string,
}
